import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/lib/api";

const FORM_URL = "https://readdy.ai/api/form/da6635t24u656k1kkfag";

export default function Contact() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("website_alt") as string || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("sending");
    setErrorMsg("");

    const fields: Record<string, string> = {};
    formData.forEach((value, key) => {
      if (typeof value === "string") fields[key] = value;
    });

    // The message is kept by our API (read in the admin panel). The form
    // service still gets a copy, best-effort, so its email alerts continue.
    const params = new URLSearchParams(fields);
    void fetch(FORM_URL, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: params.toString(),
    }).catch(() => {});

    try {
      await api.post("/contact", fields);
      setStatus("success");
      form.reset();
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err instanceof ApiError && err.status !== 0 ? err.message : t("home.contact.networkError")
      );
    }
  };

  return (
    <section id="contact" className="relative py-20 md:py-28 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Info */}
          <div>
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
              {t("home.contact.eyebrow")}
            </span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-foreground-950">
              {t("home.contact.title")}
            </h2>
            <p className="mt-4 text-foreground-600 leading-relaxed">
              {t("home.contact.subtitle")}
            </p>

            <h3 className="mt-8 text-sm font-semibold uppercase tracking-wider text-foreground-950">
              {t("home.contact.customerService")}
            </h3>
            <ul className="mt-5 space-y-5">
              <li className="flex items-center gap-4">
                <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <i className="ri-mail-line text-xl"></i>
                </span>
                <div>
                  <div className="text-xs text-foreground-500">{t("home.contact.emailUs")}</div>
                  <a href="mailto:Info@aistylemedia.com" className="text-sm font-semibold text-foreground-950 hover:text-primary-600 transition-colors">
                    Info@aistylemedia.com
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-4">
                <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <i className="ri-customer-service-2-line text-xl"></i>
                </span>
                <div>
                  <div className="text-xs text-foreground-500">{t("home.contact.supportEmail")}</div>
                  <a href="mailto:support@aistylemedia.com" className="text-sm font-semibold text-foreground-950 hover:text-primary-600 transition-colors">
                    support@aistylemedia.com
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-4">
                <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <i className="ri-building-2-line text-xl"></i>
                </span>
                <div>
                  <div className="text-xs text-foreground-500">{t("home.contact.registeredBusiness")}</div>
                  <div className="text-sm font-semibold text-foreground-950">
                    {t("home.contact.companyName")}
                  </div>
                  <div className="text-xs text-foreground-500">
                    {t("home.contact.uic")}
                  </div>
                </div>
              </li>
              <li className="flex items-center gap-4">
                <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <i className="ri-map-pin-2-line text-xl"></i>
                </span>
                <div>
                  <div className="text-xs text-foreground-500">{t("home.contact.registeredAddress")}</div>
                  <div className="text-sm font-semibold text-foreground-950">
                    {t("home.contact.address")}
                  </div>
                </div>
              </li>
              <li className="flex items-center gap-4">
                <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <i className="ri-phone-line text-xl"></i>
                </span>
                <div>
                  <div className="text-xs text-foreground-500">{t("home.contact.callUs")}</div>
                  <a href="tel:+359886886479" className="text-sm font-semibold text-foreground-950 hover:text-primary-600 transition-colors">
                    +359 886 886 479
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-4">
                <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <i className="ri-calendar-schedule-line text-xl"></i>
                </span>
                <div>
                  <div className="text-xs text-foreground-500">{t("home.contact.workingHours")}</div>
                  <div className="text-sm font-semibold text-foreground-950">{t("home.contact.hours")}</div>
                </div>
              </li>
              <li className="flex items-center gap-4">
                <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <i className="ri-time-line text-xl"></i>
                </span>
                <div>
                  <div className="text-xs text-foreground-500">{t("home.contact.responseTime")}</div>
                  <div className="text-sm font-semibold text-foreground-950">{t("home.contact.within24h")}</div>
                </div>
              </li>
            </ul>
          </div>

          {/* Form */}
          <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    {t("home.contact.name")}
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    placeholder={t("home.contact.namePlaceholder")}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    {t("home.contact.email")}
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  {t("home.contact.subject")}
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                  placeholder={t("home.contact.subjectPlaceholder")}
                />
              </div>

              <div className="mt-5">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  {t("home.contact.message")}
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  required
                  maxLength={500}
                  className="w-full resize-none rounded-md border border-background-300/70 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                  placeholder={t("home.contact.messagePlaceholder")}
                ></textarea>
                <p className="mt-1 text-xs text-foreground-400">{t("home.contact.messageHint")}</p>
              </div>

              {/* Anti-spam honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />

              <button
                type="submit"
                disabled={status === "sending"}
                className="mt-6 w-full whitespace-nowrap rounded-full bg-gradient-to-r from-primary-500 to-accent-500 py-3.5 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
              >
                {status === "sending" ? t("home.contact.sending") : t("home.contact.send")}
              </button>

              {status === "success" && (
                <div className="mt-4 flex items-center gap-2 rounded-lg bg-primary-50 px-4 py-3 text-sm text-primary-700">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                  {t("home.contact.success")}
                </div>
              )}

              {status === "error" && (
                <div className="mt-4 flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                  <i className="ri-error-warning-line text-lg"></i>
                  {errorMsg}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}